import {AvatarTone} from './avatar-colors';

const foundation = {
  fontSize: {
    headline1: '30px',
    headline2: '25px',
    headline3: '20px',
    headline4: '16px',
    body1: '16px',
    body2: '14px',
    cta: '14px',
    microcopy: '12px',
  },
  lineHeight: {
    headline1: 1.5,
    headline2: 1.3,
    headline3: 1.2,
    headline4: 1.5,
    body1: 1.6,
    body2: 1.46,
    cta: 1.7,
    microcopy: 1.5,
  },
  fontWeight: {
    regular: 400,
    semibold: 500,
    bold: 600,
  },
  fontFamily: "'Graphik', sans-serif",
  breakpoints: {
    xs: '0px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
};

const lightColors = {
  textBody: '#5f5f8c',
  textMedium: '#5e5e73',
  textDark: '#1e1e2f',
  textMuted: '#bfbfd4',
  green200: '#d3eee3',
  green100: '#f0faf6',
  green300: '#47b881',
  green400: '#1a9e68',
  green500: '#024b27',
  blue500: '#55a6f1',
  blue400: '#084b8a',
  blue300: '#1070ca',
  blue200: '#dbeaf7',
  blue100: '#f6f9fd',
  warmBlue: '#3b3dda',
  purple200: '#e4e5fb',
  purple300: '#585adf',
  purple350: '#3436a2',
  purple100: '#f6f6fe',
  purple400: '#28284d',
  red300: '#f39c9a',
  red400: '#d52a2a',
  red200: '#f7e4e4',
  red100: '#fef6f6',
  red500: '#910c0c',
  orange200: '#ffe3c8',
  orange300: '#e6821d',
  orange100: '#fef8f2',
  orange400: '#703f0f',
  darkBluePurple: '#605f8c',
  goalProgressNeutral: '#9b9db5',
  borderLight: '#ededf2',
  paleLilacTwo: '#e5e5eb',
  backgroundDark: '#f6f6f8',
  backgroundLight: '#fafafa',
  borderDark: '#cecede',
  blackTwo: '#242424',
  blackThree: '#171717',
  white: '#ffffff',
  black: '#000000',
  alwaysWhite: '#ffffff',
  overlay: 'rgba(30, 30, 47, 0.48)',
};

const darkColors: typeof lightColors = {
  textBody: '#b7b8cf',
  textMedium: '#a7a8bd',
  textDark: '#f4f4f8',
  textMuted: '#77798f',
  green200: '#174a38',
  green100: '#102c24',
  green300: '#55c993',
  green400: '#62d49e',
  green500: '#b7efd3',
  blue500: '#78b9f5',
  blue400: '#8fc6fa',
  blue300: '#64adf0',
  blue200: '#173b5d',
  blue100: '#122438',
  warmBlue: '#8f91ff',
  purple200: '#35375c',
  purple300: '#8587ff',
  purple350: '#a4a5ff',
  purple100: '#252640',
  purple400: '#d7d7ff',
  red300: '#ffaaa7',
  red400: '#ff7474',
  red200: '#552a30',
  red100: '#321b20',
  red500: '#ffb3b3',
  orange200: '#5a3a1d',
  orange300: '#f4a34f',
  orange100: '#302318',
  orange400: '#ffd0a0',
  darkBluePurple: '#b8b9df',
  goalProgressNeutral: '#a0a2bd',
  borderLight: '#2b2d3a',
  paleLilacTwo: '#303240',
  backgroundDark: '#232530',
  backgroundLight: '#111218',
  borderDark: '#3b3e50',
  blackTwo: '#f4f4f7',
  blackThree: '#ffffff',
  white: '#191a22',
  black: '#000000',
  alwaysWhite: '#ffffff',
  overlay: 'rgba(0, 0, 0, 0.68)',
};

const createTheme = (
  colors: typeof lightColors,
  elevation: {level1: string; level2: string},
  avatarTone: AvatarTone,
) => ({
  ...foundation,
  elevation,
  colors,
  avatarTone,
});

export const theme = createTheme(
  lightColors,
  {
    level1: '0px 8px 16px rgba(0, 0, 0, 0.05)',
    level2: '0px 2px 5px rgba(172, 172, 190, 0.24)',
  },
  {
    backgroundSaturation: 54,
    backgroundLightness: 84,
    foregroundSaturation: 55,
    foregroundLightness: 23,
  },
);

export const darkTheme = createTheme(
  darkColors,
  {
    level1: '0px 10px 24px rgba(0, 0, 0, 0.28)',
    level2: '0px 4px 12px rgba(0, 0, 0, 0.36)',
  },
  {
    backgroundSaturation: 45,
    backgroundLightness: 31,
    foregroundSaturation: 54,
    foregroundLightness: 96,
  },
);
